import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { parseProduct, type Product } from '@/lib/marketplace';
import type { NostrEvent } from '@nostrify/nostrify';

export interface BrowseProductEntry {
  event: NostrEvent;
  product: Product;
}

/**
 * Fetch public kind:30018 product listings for a specific merchant + stall.
 * Only runs when both merchantPubkey and stallId are provided.
 *
 * Deduplicates by pubkey+d-tag (addressable event semantics) and keeps
 * only products that belong to the given stallId.
 */
export function useBrowseProducts(merchantPubkey: string | null, stallId: string | null) {
  const { nostr } = useNostr();

  return useQuery<BrowseProductEntry[]>({
    queryKey: ['browse-products', merchantPubkey, stallId],
    queryFn: async () => {
      if (!merchantPubkey || !stallId) return [];

      const events = await nostr.query(
        [{ kinds: [30018], authors: [merchantPubkey], limit: 200 }],
        { signal: AbortSignal.timeout(15000) }
      );

      // Deduplicate by pubkey+d (keep newest)
      const latest = new Map<string, NostrEvent>();
      for (const event of events) {
        const d = event.tags.find(t => t[0] === 'd')?.[1] ?? '';
        const key = `${event.pubkey}:${d}`;
        const existing = latest.get(key);
        if (!existing || event.created_at > existing.created_at) {
          latest.set(key, event);
        }
      }

      const result: BrowseProductEntry[] = [];
      for (const event of latest.values()) {
        const product = parseProduct(event.content);
        // Only include products that belong to this stall
        if (product && product.stall_id === stallId) {
          result.push({ event, product });
        }
      }

      // Sort by name alphabetically for consistent display
      result.sort((a, b) => a.product.name.localeCompare(b.product.name));
      return result;
    },
    enabled: !!merchantPubkey && !!stallId,
    staleTime: 60000,
    retry: 2,
  });
}
